import React, { useCallback, useContext } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { spaces } from '../../assets/styles/themes/tokens';
import EditableSection from '../EditableSection';
import useScreenSize from '../../utils/useScreenSize';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/user.selectors';
import { password } from '../../validators';
import services from '../../services';
import EmailFieldsRenderer from '../EmailFieldsRenderer';
import { NotificationContext } from '../../state/NotificationContextProvider';

export interface ChangeEmailPayload {
  email: string;
  password: string;
}

interface ServerErrorMessage {
  field: keyof ChangeEmailPayload;
  message: string;
}

const ChangeEmailSection = () => {
  const { t } = useTranslation();
  const { showNotification } = useContext(NotificationContext);
  const { isXsScreen, isSmScreen } = useScreenSize();

  const user = useSelector(selectUser);
  const canEditEmail = () => {
    return !!user && !user.isSchoolUser && !user.isLibraryUser && !user.isKeycloakSSO;
  };
  const userEmail: string = user?.email || '';

  const changeEmailFormValidator = yup.object<ChangeEmailPayload>({
    email: yup
      .string()
      .email('The email is not valid')
      .required('This field is required')
      .notOneOf([userEmail], 'This email address is the same as your current email address'),
    password: password()
  });

  const {
    register,
    errors,
    getValues,
    triggerValidation,
    setError
  } = useForm<ChangeEmailPayload>({
    mode: 'onBlur',
    validationSchema: changeEmailFormValidator,
    defaultValues: { email: userEmail }
  });

  const onSaveChanges = useCallback(async ({ toggleEditMode }: {
    toggleEditMode: () => void;
  }) => {
    const isValid = await triggerValidation(['email', 'password']);

    if (!isValid) return;

    const values = getValues();

    if (!user) return;

    try {
      await services.user.changeEmail(
        user.email,
        values.email,
        values.password,
        false
      );
      toggleEditMode();
      showNotification({
        message: t('All changes were saved successfully'),
        severity: 'success',
      });
    } catch (error: any) {
      showNotification({
        message: t('An error occurred while saving changes'),
        severity: 'error',
      });
      const serverErrorMessage = getServerErrorMessage(error.message);
      if (serverErrorMessage) {
        return setError(serverErrorMessage.field, 'manual', t(serverErrorMessage.message));
      }
    }
  }, [user]);

  const getServerErrorMessage = (errorMessage: string): ServerErrorMessage | undefined => {
    if (errorMessage.match(/Invalid username or password/)) {
      return {
        field: 'password',
        message: 'The password you entered is incorrect.'
      };
    }
    if (errorMessage.match(/Email address already exists/)) {
      return {
        field: 'email',
        message: 'The email you entered already exists.'
      };
    }
    return;
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: (isXsScreen || isSmScreen) ? '1fr' : '1fr 1fr',
        gap: spaces.md
      }}
    >
      <EditableSection title={t('My Account')} onSaveChanges={onSaveChanges}>
        {({ isEditMode }) => (
          <EmailFieldsRenderer
            isEditMode={isEditMode}
            userEmail={userEmail}
            canEditEmail={canEditEmail()}
            register={register}
            triggerValidation={triggerValidation}
            errors={errors}
          />
        )}
      </EditableSection>
    </Box>
  );
};

export default ChangeEmailSection;
